import { logger } from "@/lib/logger"
import { savedViewStorageStateSchema } from "@/utils/validation/externalBoundarySchemas"
import { warnOnInvalidExternalData } from "@/utils/validation/externalBoundaryValidation"

import type { SavedViewConfig } from "../types/savedViews"

const log = logger("SavedViewsStorage")

/** Shape persisted by the auto-save timer (view config + loaded-view id). */
interface SavedViewStorageState extends SavedViewConfig {
  loadedViewId?: string | null
}

/**
 * Every method takes the caller-resolved storage key as its first argument
 * (see `useScopedStorageKey`, scoped by org + workspace) rather than a
 * hardcoded literal, so switching workspace can never read or write another
 * workspace's saved-view state. A missing key (org/workspace not resolved
 * yet) means "don't persist" — reads return null and writes no-op.
 */
export const savedViewsStorage = {
  saveLastView: (key: string | undefined, viewId: string) => {
    if (!key) {
      return
    }
    try {
      localStorage.setItem(key, viewId)
    } catch (error) {
      log.warn("Failed to save last view to localStorage", { error })
    }
  },

  getLastView: (key: string | undefined): string | null => {
    if (!key) {
      return null
    }
    try {
      return localStorage.getItem(key)
    } catch (error) {
      log.warn("Failed to get last view from localStorage", { error })
      return null
    }
  },

  saveCurrentState: (key: string | undefined, state: unknown) => {
    if (!key) {
      return
    }
    try {
      localStorage.setItem(key, JSON.stringify(state))
    } catch (error) {
      log.warn("Failed to save current state to localStorage", { error })
    }
  },

  getCurrentState: (key: string | undefined): SavedViewStorageState | null => {
    if (!key) {
      return null
    }
    try {
      const state = localStorage.getItem(key)
      return state
        ? warnOnInvalidExternalData(
            JSON.parse(state) as SavedViewStorageState,
            savedViewStorageStateSchema,
            {
              boundary: "Uploaded conversation saved view state",
              log,
            },
          )
        : null
    } catch (error) {
      log.warn("Failed to get current state from localStorage", { error })
      return null
    }
  },

  clearCurrentState: (key: string | undefined) => {
    if (!key) {
      return
    }
    try {
      localStorage.removeItem(key)
    } catch (error) {
      log.warn("Failed to clear current state from localStorage", { error })
    }
  },
}
